import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import "@/App.css";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { I18nProvider } from "@/i18n";
import { ThemeProvider, useTheme } from "@/contexts/ThemeContext";
import Layout from "@/components/Layout";
import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import MagicVerify from "@/pages/MagicVerify";
import Register from "@/pages/Register";
import Dashboard from "@/pages/Dashboard";
import PoolDetail from "@/pages/PoolDetail";
import MatchDetail from "@/pages/MatchDetail";
import Join from "@/pages/Join";
import GlobalAdmin from "@/pages/GlobalAdmin";
import Instructions from "@/pages/Instructions";
import Profile from "@/pages/Profile";
import TournamentSummary from "@/pages/TournamentSummary";
import { Loader2 } from "lucide-react";

function ThemedToaster() {
    const { theme } = useTheme();
    return <Toaster richColors theme={theme} position="top-center" />;
}

function ProtectedRoute({ children, adminOnly = false }) {
    const { user, loading } = useAuth();
    const loc = useLocation();
    if (loading) {
        return (
            <div className="min-h-[60vh] flex items-center justify-center">
                <Loader2 className="h-6 w-6 animate-spin text-[var(--accent-hex)]" />
            </div>
        );
    }
    if (!user) {
        const next = encodeURIComponent(loc.pathname + loc.search);
        return <Navigate to={`/login?next=${next}`} replace />;
    }
    if (adminOnly && user.role !== "admin") {
        return <Navigate to="/dashboard" replace />;
    }
    return children;
}

function App() {
    return (
        <BrowserRouter>
            <ThemeProvider>
                <I18nProvider>
                    <AuthProvider>
                        <ThemedToaster />
                        <Layout>
                            <Routes>
                                <Route path="/" element={<Landing />} />
                                <Route path="/login" element={<Login />} />
                                <Route path="/auth/magic" element={<MagicVerify />} />
                                <Route path="/register" element={<Register />} />
                                <Route path="/join/:code" element={<Join />} />
                                <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
                                <Route path="/pools/:poolId" element={<ProtectedRoute><PoolDetail /></ProtectedRoute>} />
                                <Route path="/matches/:matchId" element={<ProtectedRoute><MatchDetail /></ProtectedRoute>} />
                                <Route path="/admin" element={<ProtectedRoute adminOnly><GlobalAdmin /></ProtectedRoute>} />
                                <Route path="/owadmin" element={<ProtectedRoute adminOnly><GlobalAdmin /></ProtectedRoute>} />
                                <Route path="/help" element={<Instructions />} />
                                <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
                                <Route path="/summary" element={<TournamentSummary />} />
                                <Route path="*" element={<Navigate to="/" replace />} />
                            </Routes>
                        </Layout>
                    </AuthProvider>
                </I18nProvider>
            </ThemeProvider>
        </BrowserRouter>
    );
}

export default App;
